import React from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Code } from "lucide-react";
import { useTranslation } from "react-i18next";

interface TemplateVariable {
  labelKey: string;
  value: string;
}

interface TemplateVariableCategory {
  labelKey: string;
  variables: TemplateVariable[];
}

// Keep this list aligned with TemplateService.createTemplateView in guesteria-backend.
const TEMPLATE_VARIABLE_CATEGORIES: TemplateVariableCategory[] = [
  {
    labelKey: "chats.dynamicVariables.categories.reservation",
    variables: [
      ["guestFirstName", "{{Guest_First_Name}}"],
      ["guestLastName", "{{Guest_Last_Name}}"],
      ["guestEmail", "{{Guest_Email}}"],
      ["guestPhone", "{{Guest_Phone}}"],
      ["guestBookingSource", "{{Guest_Booking_Source}}"],
      ["numberOfGuests", "{{Number_Of_Guests}}"],
      ["checkOut", "{{Check_Out_Date}}"],
      ["checkIn", "{{Check_In_Date}}"],
      ["rentPrice", "{{Rent_Price}}"],
      ["extraCharges", "{{Extra_Charges}}"],
      ["totalPrice", "{{Total_Price}}"],
      ["preCheckInLink", "{{Pre_Check_In_Link}}"],
      ["preCheckOutLink", "{{Pre_Check_Out_Link}}"],
      ["securityDepositLink", "{{Security_Deposit_Link}}"],
      ["reservationExternalId", "{{Reservation_External_Id}}"],
      ["reservationExternalId2", "{{Reservation_External_Id_2}}"],
      [
        "reservationExternalGeneralNotes",
        "{{Reservation_External_General_Notes}}",
      ],
      ["chatLink", "{{Chat_Link}}"],
    ].map(([label, value]) => ({
      labelKey: `chats.dynamicVariables.parameters.${label}`,
      value,
    })),
  },
  {
    labelKey: "chats.dynamicVariables.categories.room",
    variables: [
      ["roomName", "{{Room_Name}}"],
      ["rentalKeyBoxCode", "{{Rental_KeyBox_Code}}"],
      ["buildingEntrance", "{{Building_Entrance}}"],
      ["street", "{{Street}}"],
      ["roomNumber", "{{Room_Number}}"],
      ["postalCode", "{{Postal_Code}}"],
      ["floor", "{{Floor}}"],
      ["wifiCode", "{{Wifi_Code}}"],
      ["wifiName", "{{Wifi_Name}}"],
    ].map(([label, value]) => ({
      labelKey: `chats.dynamicVariables.parameters.${label}`,
      value,
    })),
  },
  {
    labelKey: "chats.dynamicVariables.categories.keyProvider",
    variables: [
      {
        labelKey: "chats.dynamicVariables.parameters.buildingCode",
        value: "{{Building_Code}}",
      },
    ],
  },
  {
    labelKey: "chats.dynamicVariables.categories.guestApp",
    variables: [
      ["pageName", "{{pageName}}"],
      ["wifiPage", "{{WiFi}}"],
      ["hotelMap", "{{Hotel Map}}"],
      ["faq", "{{FAQ}}"],
      ["hotelInfo", "{{Hotel info}}"],
      ["guests", "{{Guests}}"],
      ["service", "{{Service}}"],
      ["amenities", "{{Amenities}}"],
      ["activities", "{{Activities}}"],
      ["firstPreCheckoutStep", "{{First Step Pre Check-Out}}"],
      ["scanOrAddDocument", "{{Scan or Add document}}"],
      ["upgradeRoom", "{{Upgrade Room}}"],
      ["myProfile", "{{My profile}}"],
      ["settings", "{{Settings}}"],
      ["billSummary", "{{Bill Summary}}"],
      ["totalBillSummary", "{{Total_Bill_Summary}}"],
      ["checkOutRules", "{{Check Out Rules}}"],
      ["checkInRules", "{{Check in Rules}}"],
      ["privacyAndTerms", "{{PP & ToU}}"],
      ["chat", "{{Chat}}"],
      ["myRequests", "{{My Requests}}"],
    ].map(([label, value]) => ({
      labelKey: `chats.dynamicVariables.parameters.${label}`,
      value,
    })),
  },
];

interface VariableSelectorProps {
  onSelect: (variable: string) => void;
}

export const VariableSelector: React.FC<VariableSelectorProps> = ({
  onSelect,
}) => {
  const { t } = useTranslation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          type="button"
          title={t("chats.dynamicVariables.insert")}
          aria-label={t("chats.dynamicVariables.insert")}
        >
          <Code size={20} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-[min(32rem,var(--radix-dropdown-menu-content-available-height))] w-80 overflow-y-auto"
      >
        {TEMPLATE_VARIABLE_CATEGORIES.map((category, index) => (
          <React.Fragment key={category.labelKey}>
            {index > 0 && <DropdownMenuSeparator />}
            <DropdownMenuGroup>
              <DropdownMenuLabel>{t(category.labelKey)}</DropdownMenuLabel>
              {category.variables.map((variable) => (
                <DropdownMenuItem
                  key={variable.value}
                  className="flex flex-col items-start gap-0.5"
                  onClick={() => onSelect(variable.value)}
                >
                  <span>{t(variable.labelKey)}</span>
                  <code className="text-muted-foreground text-xs">
                    {variable.value}
                  </code>
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
